import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Users, Mail, FileText, ShieldOff } from 'lucide-react'
import { ProspectTable } from '../components/ProspectTable'
import { useProspects, useProspectStats } from '../hooks/useProspects'
import type { OutreachStatus } from '../api/prospectsApi'

const OUTREACH_FILTERS: Array<{ value: OutreachStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'none', label: 'Not contacted' },
  { value: 'queued', label: 'Queued' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'replied', label: 'Replied' },
  { value: 'interested', label: 'Interested' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
]

export function ProspectsPage() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [outreach, setOutreach] = useState<OutreachStatus | 'all'>('all')
  const [contactableOnly, setContactableOnly] = useState(false)

  const { data: stats, isLoading: statsLoading } = useProspectStats()
  const { data, isLoading } = useProspects({
    q: q || undefined,
    outreachStatus: outreach === 'all' ? undefined : outreach,
    contactable: contactableOnly || undefined,
    limit: 50,
  })

  const cards = [
    { label: 'Prospects', value: stats?.total, icon: Users },
    { label: 'Contactable', value: stats?.contactable, icon: Mail },
    { label: 'With a biography', value: stats?.withBio, icon: FileText },
  ]

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Prospects</h1>
          <p className="text-sm text-slate-500">
            Scraped artists who have not signed up. Nothing here is public.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/app/prospects/claims')}>
          Claim requests
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {cards.map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">{label}</CardTitle>
              <Icon className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-semibold tabular-nums">
                  {value?.toLocaleString() ?? '—'}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* A standing reminder, not a one-off banner. Everyone in this table is a person who
          never asked to be here, and the surface should keep saying so. */}
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm text-amber-900">
            <ShieldOff className="h-4 w-4" />
            Scraped personal data
          </CardTitle>
          <CardDescription className="text-amber-800">
            These are people who never signed up. Publishing any of these pages is gated on
            the HT-2 compliance review. &ldquo;Do not contact&rdquo; and &ldquo;no
            page&rdquo; are separate promises — honour the one that was actually asked for.
          </CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search handle, name or bio…"
              className="max-w-xs"
            />
            <Button
              variant={contactableOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setContactableOnly((v) => !v)}
            >
              Contactable only
            </Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {OUTREACH_FILTERS.map((f) => (
              <Button
                key={f.value}
                variant={outreach === f.value ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setOutreach(f.value)}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <ProspectTable
            prospects={data?.prospects ?? []}
            isLoading={isLoading}
            onSelect={(p) => navigate(`/app/prospects/${p.id}`)}
          />
          {data?.pagination && (
            <p className="pt-3 text-xs text-slate-500">
              Showing {data.prospects.length} of {data.pagination.total.toLocaleString()}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
